import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {Form} from 'react-bootstrap'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import clsx from 'clsx'
import Swal from 'sweetalert2'
import {useQueryClient} from 'react-query'
import {InputCurrency, getApiErrorMessage, isNotEmpty} from '../../../../_metronic/helpers'
import {Project} from '../../../../app/modules/project/components/core/_models'
import {
  getProjectByClientId,
  getProjectById,
} from '../../../../app/modules/project/components/core/_requests'
import {Loading} from '../../components/loading/Loading'
import {Toast} from '../../widgets'
import {getTaskAttachmentError, TASK_ATTACHMENT_ACCEPT} from '../../../helpers/taskAttachment'
import {
  ResponseContract,
  initialContract,
  Contracts,
  ContractAiResult,
  ContractRushmindEstimate,
  contractFromResponse,
  contractFileUrl,
  getCreatedContractId,
  isContractAiQueued,
} from '../../../../app/modules/contracts/components/contracts-list/core/_models'
import {useListView} from '../../../../app/modules/contracts/components/contracts-list/core/ListViewProvider'
import {useQueryResponse} from '../../../../app/modules/contracts/components/contracts-list/core/QueryResponseProvider'
import {
  createContract,
  getAiAnalysisStatus,
  getRushmindAnalysisEstimate,
  updateContract,
} from '../../../../app/modules/contracts/components/contracts-list/core/_requests'
import {invalidateContractsQueries} from '../../../../app/modules/contracts/components/contracts-list/core/invalidateContractsQueries'
import {getContractAiStatusMessage} from './contractAiMessages'
import {useAuth} from '../../../../app/modules/auth'
import {ContractRushmindProgressPanel} from './ContractRushmindProgressPanel'

const RUSHMIND_MAX_FILE_BYTES = 5 * 1024 * 1024

type Props = {
  isContractLoading: boolean
  Contracts: ResponseContract
  projectId: string | undefined
  clientId: string | undefined
  handleClose: () => void | undefined
}

const ContractsModalContent: FC<Props> = ({
  Contracts,
  isContractLoading,
  handleClose,
  projectId,
  clientId,
}) => {
  const intl = useIntl()
  const {setItemIdForUpdate} = useListView()
  const {refetch} = useQueryResponse()
  const {client_id} = useParams()

  const queryClient = useQueryClient()
  const {currentUser} = useAuth()
  const modules = currentUser?.instance?.plan?.modules
  const isNewContract = !isNotEmpty(contractFromResponse(Contracts).id)
  const hasContractsModule = modules?.some(
    (module) => module.slug_name === 'gestao-de-contratos' && module.pivot?.active
  )
  const showRushmindOption =
    isNewContract && hasContractsModule && !!(projectId || client_id)

  const [analyzeWithRushmind, setAnalyzeWithRushmind] = useState(false)
  const [rushmindEstimate, setRushmindEstimate] = useState<ContractRushmindEstimate | null>(null)
  const [estimateLoading, setEstimateLoading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [postCreateRushmind, setPostCreateRushmind] = useState<{
    contractId: number
    estimate?: ContractRushmindEstimate | null
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const editContractschema = Yup.object().shape({
    title: Yup.string()
      .min(2, intl.formatMessage({id: 'FORM.INPUT.VALIDATION.MIN_LENGTH'}, {min: '2'}))
      .max(50, intl.formatMessage({id: 'FORM.INPUT.VALIDATION.MAX_LENGTH'}, {max: '50'}))
      .required(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})),
    value: Yup.string()
      .min(2, intl.formatMessage({id: 'FORM.INPUT.VALIDATION.MIN_LENGTH'}, {min: '2'}))
      .required(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})),
    end_date: Yup.string()
      .nullable()
      .transform((value) => (value === '' || value == null ? null : value)),
    begin_date: Yup.date().required(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})),
    type: Yup.number().required(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})),
  })

  const [loading, setLoading] = useState<Boolean>(false)
  const [dataProject, setDataProject] = useState<Project[]>([])

  const getSwalTheme = () => {
    const CurrentTheme = window.localStorage.getItem('kt_theme_mode_value') || ''
    return CurrentTheme === 'dark' ? '#353b48' : '#fff'
  }

  const handleAiAfterCreate = async (response: ResponseContract) => {
    const ai = response.ai
    const contractId = getCreatedContractId(response)

    const showRushmindProgress = (estimate?: ContractRushmindEstimate | null) => {
      if (!contractId) {
        return false
      }
      invalidateContractsQueries(queryClient)
      refetch()
      setPostCreateRushmind({
        contractId,
        estimate: estimate ?? rushmindEstimate,
      })
      return true
    }

    if (isContractAiQueued(ai) && showRushmindProgress(ai.estimate)) {
      return
    }

    if (contractId && analyzeWithRushmind) {
      try {
        const statusResponse = await getAiAnalysisStatus(contractId)
        if (
          statusResponse.status === 'queued' ||
          statusResponse.status === 'processing'
        ) {
          if (showRushmindProgress()) {
            return
          }
        }
      } catch {
        // Resposta de criação sem bloco ai ou formato legado — segue fluxo padrão abaixo.
      }
    }

    if (!ai) {
      Swal.fire({
        icon: 'success',
        title: 'Criado!',
        html: '<h5>' + (response?.message ?? '') + '</h5>',
        background: getSwalTheme(),
        confirmButtonColor: '#009ef7',
        color: '#fff',
      })
      invalidateContractsQueries(queryClient)
      cancel(true)
      return
    }

    const result = ai as ContractAiResult
    const detail = getContractAiStatusMessage(result.status, result.message)

    Swal.fire({
      icon: 'success',
      title: 'Contrato salvo',
      html: `<h5>${response.message ?? ''}</h5><p class="mt-3 text-muted">${detail}</p>`,
      background: getSwalTheme(),
      confirmButtonColor: '#009ef7',
      color: '#fff',
    })
    invalidateContractsQueries(queryClient)
    cancel(true)
  }
  const selectProject = async () => {
    if (dataProject.length === 0) {
      setLoading(true)
      await getProjectByClientId(client_id).then((response) => {
        setDataProject(response.data)
      })
    }
  }

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    handleClose()
  }
  const [data, setData] = useState<Contracts>(initialContract)
  const updateData = (fieldsToUpdate: Partial<Contracts>) => {
    const updatedData = {...data, ...fieldsToUpdate}
    setData(updatedData)
  }

  const formik = useFormik<Contracts>({
    initialValues: contractFromResponse(Contracts),
    enableReinitialize: true,
    validationSchema: editContractschema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          const response = await updateContract(values)
          Swal.fire({
            icon: 'success',
            title: 'Editado!',
            html: '<h5>' + response?.message + '</h5>',
            background: getSwalTheme(),
            confirmButtonColor: '#009ef7',
            color: '#fff',
          })
          invalidateContractsQueries(queryClient)
          cancel(true)
        } else {
          const response = await createContract({
            ...values,
            analyze_with_rushmind: analyzeWithRushmind,
          })
          handleAiAfterCreate(response)
        }
      } catch (ex) {
        console.error(ex)
        Toast.fire({
          icon: 'error',
          iconHtml: '<span>!</span>',
          title: getApiErrorMessage(ex),
          customClass: {
            popup: 'bg-light-danger',
            title: 'text-danger',
          },
        })
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (projectId) {
      getProjectById(parseInt(projectId)).then((response) => {
        formik.setFieldValue('client_id', clientId)
        formik.setFieldValue('project_id', projectId)
      })
    }
    if (client_id) {
      formik.setFieldValue('client_id', client_id)
      formik.setFieldValue('project_id', contractFromResponse(Contracts).project_id)
    }
  }, [])

  const fetchRushmindEstimate = useCallback(async (file: File) => {
    setEstimateLoading(true)
    try {
      const extension = file.name.includes('.') ? file.name.split('.').pop() : undefined
      const response = await getRushmindAnalysisEstimate(file.size, extension)
      setRushmindEstimate(response.data)
    } catch {
      setRushmindEstimate(null)
    } finally {
      setEstimateLoading(false)
    }
  }, [])

  const handleRushmindToggle = useCallback((checked: boolean) => {
    setAnalyzeWithRushmind(checked)
  }, [])

  const applySelectedFile = useCallback(
    (file: File | undefined) => {
      // Mesma regra do servidor para o arquivo do contrato (tipo e 20MB), na seleção e no arrastar.
      const attachmentError = file ? getTaskAttachmentError(file) : null
      if (attachmentError) {
        Toast.fire({
          icon: 'error',
          title: attachmentError,
          timer: 8000,
          customClass: {
            popup: 'bg-light-danger',
            title: 'text-danger',
          },
        })
        return
      }
      formik.setFieldValue('file', file)
      setSelectedFileName(file?.name ?? null)
      if (file && file.size > RUSHMIND_MAX_FILE_BYTES) {
        setAnalyzeWithRushmind(false)
        setRushmindEstimate(null)
      } else if (file) {
        fetchRushmindEstimate(file)
      } else {
        setRushmindEstimate(null)
      }
    },
    [fetchRushmindEstimate, formik]
  )

  const handleCreate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      applySelectedFile(e.target.files?.[0])
    },
    [applySelectedFile]
  )

  const handleFileDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (!file) return
      applySelectedFile(file)
      if (fileInputRef.current) {
        const dt = new DataTransfer()
        dt.items.add(file)
        fileInputRef.current.files = dt.files
      }
    },
    [applySelectedFile]
  )

  const handleClosePostCreate = () => {
    setPostCreateRushmind(null)
    cancel(true)
  }

  if (postCreateRushmind) {
    return (
      <>
        <ContractRushmindProgressPanel
          contractId={postCreateRushmind.contractId}
          initialEstimate={postCreateRushmind.estimate}
          showAfterCreateIntro
          showSuggestionsWhenComplete
          onSuggestionsFinished={handleClosePostCreate}
        />
        <div className='text-end pt-10'>
          <button type='button' className='btn btn-light py-2' onClick={handleClosePostCreate}>
            Fechar
          </button>
        </div>
      </>
    )
  }

  const selectedFile = formik.values.file instanceof File ? formik.values.file : undefined
  const existingContractFileUrl = contractFileUrl(formik.values.file)
  const isRushmindBlockedByFileSize =
    !!selectedFile && selectedFile.size > RUSHMIND_MAX_FILE_BYTES

  return (
    <>
      <form
        id='kt_modal_add_contract_form'
        className='form'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_contract_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_contract_header'
          data-kt-scroll-wrappers='#kt_modal_add_contract_scroll'
          data-kt-scroll-offset='300px'
        >
          <div className='row justify-content-between'>
            <div className='col mb-5 mb-lg-0'>
              {isNewContract ? (
                <div className='mb-4'>
                  <label className='required fw-bold mb-1' htmlFor='file'>
                    Escolher arquivo
                  </label>

                  <input
                    ref={fileInputRef}
                    type='file'
                    name='file'
                    id='file'
                    className='d-none'
                    accept={TASK_ATTACHMENT_ACCEPT}
                    onChange={handleCreate}
                  />

                  <div
                    role='button'
                    tabIndex={0}
                    className={clsx(
                      'border border-dashed rounded py-6 px-4 text-center cursor-pointer',
                      isDragOver ? 'border-primary bg-light-primary' : 'border-gray-300 bg-light',
                      formik.touched.file && formik.errors.file && 'border-danger'
                    )}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        fileInputRef.current?.click()
                      }
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault()
                      setIsDragOver(true)
                    }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      setIsDragOver(true)
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault()
                      setIsDragOver(false)
                    }}
                    onDrop={handleFileDrop}
                  >
                    <i className='bi bi-cloud-arrow-up fs-2x text-primary mb-2 d-block' />
                    <span className='fw-semibold text-gray-800 d-block'>
                      Arraste o documento aqui ou clique para escolher
                    </span>
                    <span className='text-muted fs-8 d-block mt-1'>
                      PDF, DOC, DOCX, imagens e planilhas
                    </span>
                    {selectedFileName && (
                      <span className='badge badge-light-primary mt-3'>{selectedFileName}</span>
                    )}
                  </div>
                  {formik.touched.file && formik.errors.file && (
                    <div className='fv-plugins-message-container mt-2'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.file}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className='mb-4'>
                  <label className='fw-bold mb-1'>Arquivo do contrato</label>
                  <div className='border border-dashed rounded py-4 px-4 bg-light'>
                    {existingContractFileUrl ? (
                      <a
                        href={existingContractFileUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='fw-semibold text-primary'
                      >
                        <i className='bi bi-file-earmark-text me-2' />
                        Visualizar documento atual
                      </a>
                    ) : (
                      <span className='text-muted'>Nenhum arquivo vinculado a este contrato.</span>
                    )}
                    <p className='text-muted fs-8 mb-0 mt-2'>
                      O arquivo não pode ser alterado na edição.
                    </p>
                  </div>
                </div>
              )}

              <div className='mb-4'>
                <label className='required fw-bold mb-1'>
                  {intl.formatMessage({id: 'FORM.INPUT.NAME.NAME'})}
                </label>

                <input
                  placeholder={intl.formatMessage({id: 'FORM.INPUT.NAME.NAME'})}
                  {...formik.getFieldProps('title')}
                  type='text'
                  name='title'
                  className={clsx(
                    'form-control form-control-solid py-2  rounded fw-normal',
                    {'is-invalid': formik.touched.title && formik.errors.title},
                    {
                      'is-valid': formik.touched.title && !formik.errors.title,
                    }
                  )}
                  autoComplete='off'
                  disabled={formik.isSubmitting || isContractLoading}
                />
                {formik.touched.title && formik.errors.title && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.title}</span>
                    </div>
                  </div>
                )}
              </div>

              {!projectId && (
                <div className='mb-4'>
                  <label className='fw-bold mb-1'>
                    {intl.formatMessage({id: 'FORM.INPUT.NAME.PROJECTS'})}
                  </label>
                  <select
                    {...formik.getFieldProps('project_id')}
                    disabled={formik.isSubmitting || isContractLoading}
                    onChange={(e: any) => {
                      formik.setFieldValue('project_id', e.target.value)
                    }}
                    data-placeholder={intl.formatMessage({id: 'FORM.INPUT.NAME.CLIENT'})}
                    onClick={selectProject}
                    className={clsx(
                      'form-control form-control-solid py-2 rounded fw-normal',
                      {'is-invalid': formik.touched.project_id && formik.errors.project_id},
                      {
                        'is-valid': formik.touched.project_id && !formik.errors.project_id,
                      }
                    )}
                  >
                    {!loading && (
                      <option key={formik.values.project?.id}>{formik.values.project?.name}</option>
                    )}
                    {loading ? <option>Carregando Projetos</option> : ''}
                    {dataProject.map((rs, index) => {
                      return (
                        <>
                          <option value={rs.id}>{rs.name}</option>
                        </>
                      )
                    })}
                  </select>
                  {formik.touched.client_id && formik.errors.client_id && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.client_id}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className='row'>
                <div className='col mb-4'>
                  <label className='required fw-bold mb-1'>
                    {intl.formatMessage({id: 'FORM.INPUT.NAME.BEGIN_DATE'})}
                  </label>

                  <input
                    placeholder='Selecione a data de início'
                    {...formik.getFieldProps('begin_date')}
                    type='date'
                    name='begin_date'
                    className={clsx(
                      'form-control form-control-solid py-2  rounded fw-normal',
                      {'is-invalid': formik.touched.begin_date && formik.errors.begin_date},
                      {
                        'is-valid': formik.touched.begin_date && !formik.errors.begin_date,
                      }
                    )}
                    autoComplete='off'
                    disabled={formik.isSubmitting || isContractLoading}
                  />
                  {formik.touched.begin_date && formik.errors.begin_date && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.begin_date}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='col mb-4'>
                  <label className='fw-bold mb-1'>
                    {intl.formatMessage({id: 'FORM.INPUT.NAME.END_CONTRACT'})}
                  </label>

                  <input
                    placeholder='Selecione a data de fim'
                    {...formik.getFieldProps('end_date')}
                    type='date'
                    name='end_date'
                    className={clsx(
                      'form-control form-control-solid py-2  rounded fw-normal',
                      {'is-invalid': formik.touched.end_date && formik.errors.end_date},
                      {
                        'is-valid': formik.touched.end_date && !formik.errors.end_date,
                      }
                    )}
                    autoComplete='off'
                    disabled={formik.isSubmitting || isContractLoading}
                  />
                  {formik.touched.end_date && formik.errors.end_date && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.end_date}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='d-flex align-items-center justify-content-between cursor-pointer'>
                <div className='row mt-5 mt-lg-0'>
                  <p className='required fs-5 fw-bold mb-5'>
                    {intl.formatMessage({id: 'FORM.INPUT.NAME.CONTRACT_TYPE'})}
                  </p>

                  <div className='col-md-6 mb-4'>
                    <div className='form-check form-check-custom form-check-solid form-check-sm'>
                      <input
                        {...formik.getFieldProps('type')}
                        type='radio'
                        name='type'
                        id='closed_scope'
                        className={clsx('form-check-input')}
                        autoComplete='off'
                        value={1}
                        defaultChecked={formik.values.type === 1}
                        onChange={(e: any) => {
                          updateData({
                            type: parseInt(e.target.value),
                          })
                          formik.setFieldValue('type', e.target.value)
                        }}
                        disabled={formik.isSubmitting || isContractLoading}
                      />
                      {formik.touched.type && formik.errors.type && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.type}</span>
                          </div>
                        </div>
                      )}

                      <label className='form-check-label' htmlFor='closed_scope'>
                        {intl.formatMessage({id: 'FORM.INPUT.NAME.CLOSED_SCOPE'})}
                      </label>
                    </div>
                  </div>

                  <div className='col-md-6 mb-4'>
                    <div className='form-check form-check-custom form-check-solid form-check-sm'>
                      <input
                        {...formik.getFieldProps('type')}
                        type='radio'
                        name='type'
                        value={2}
                        id='recurrent'
                        defaultChecked={formik.values.type === 2}
                        onChange={(e: any) => {
                          updateData({
                            type: parseInt(e.target.value),
                          })
                          formik.setFieldValue('type', e.target.value)
                        }}
                        className={clsx('form-check-input')}
                        autoComplete='off'
                        disabled={formik.isSubmitting || isContractLoading}
                      />
                      {formik.touched.type && formik.errors.type && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.type}</span>
                          </div>
                        </div>
                      )}

                      <label className='form-check-label' htmlFor='recurrent'>
                        {intl.formatMessage({id: 'FORM.INPUT.NAME.RECURRENT_CONTRACT'})}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className='mb-4'>
                <label
                  htmlFor='budget'
                  className='required d-flex align-items-center fs-5 fw-semibold mb-2'
                >
                  {data.type === 1 || formik.values.type === 1 ? (
                    <>
                      <span>{intl.formatMessage({id: 'FORM.INPUT.NAME.CLOSED_BUDGET'})}</span>
                      <i
                        className='fas fa-exclamation-circle ms-2 fs-7'
                        data-bs-toggle='tooltip'
                        title={intl.formatMessage({id: 'FORM.INPUT.TOOLTIP.CLOSED_BUDGET'})}
                      ></i>
                    </>
                  ) : (
                    <>
                      <span>{intl.formatMessage({id: 'FORM.INPUT.NAME.MONTHLY_BUDGET'})}</span>
                      <i
                        className='fas fa-exclamation-circle ms-2 fs-7'
                        data-bs-toggle='tooltip'
                        title={intl.formatMessage({id: 'FORM.INPUT.TOOLTIP.MONTHLY_BUDGET'})}
                      ></i>
                    </>
                  )}
                </label>

                <InputCurrency
                  placeholder='Ex: R$20.000,00'
                  {...formik.getFieldProps('value')}
                  name='value'
                  className={clsx(
                    'form-control form-control-solid py-2 rounded fw-normal',
                    {'is-invalid': formik.touched.value && formik.errors.value},
                    {
                      'is-valid': formik.touched.value && !formik.errors.value,
                    }
                  )}
                  value={formik.values.value ? formik.values.value : 0}
                  disabled={formik.isSubmitting || isContractLoading}
                  onChange={(e) => {
                    formik.setFieldValue(
                      'value',
                      e.target.value.replace('.', '').replace('.', '').replace(',', '.')
                    )
                  }}
                />
                {formik.touched.value && formik.errors.value && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.value}</span>
                    </div>
                  </div>
                )}
              </div>

              {showRushmindOption && (
                <div className='mb-4 p-4 rounded border border-dashed border-gray-300 bg-light'>
                  <div className='form-check form-check-custom form-check-solid mb-0 d-flex align-items-center'>
                    <input
                      type='checkbox'
                      id='contract-analyze-with-rushmind'
                      className='form-check-input cursor-pointer'
                      checked={analyzeWithRushmind}
                      onChange={(e) => handleRushmindToggle(e.target.checked)}
                      disabled={
                        formik.isSubmitting || isContractLoading || isRushmindBlockedByFileSize
                      }
                    />
                    <label
                      className='form-check-label fw-semibold text-gray-800 cursor-pointer mb-0 ms-2'
                      htmlFor='contract-analyze-with-rushmind'
                    >
                      Quero que o RushMind analise este documento e sugira tarefas
                    </label>
                  </div>
                  <p className='text-muted fs-7 mb-0 mt-2 ms-4'>
                    Se desmarcado, o contrato será salvo normalmente, sem análise automática.
                  </p>
                  {isRushmindBlockedByFileSize && (
                    <p className='text-warning fs-7 mb-0 mt-2 ms-4'>
                      Análise do RushMind indisponível: o arquivo excede 5MB. Salve o contrato sem
                      análise ou envie um arquivo menor.
                    </p>
                  )}
                  {!isRushmindBlockedByFileSize && (
                    <div className='ms-4 mt-3'>
                      {estimateLoading ? (
                        <span className='text-muted fs-7'>Calculando tempo estimado...</span>
                      ) : rushmindEstimate ? (
                        <p className='text-gray-700 fs-7 mb-0'>
                          <strong>Tempo médio estimado da análise:</strong>{' '}
                          {rushmindEstimate.estimated_wait_label}
                          {rushmindEstimate.queue_ahead > 0 && (
                            <>
                              {' '}
                              ({rushmindEstimate.queue_ahead} documento
                              {rushmindEstimate.queue_ahead > 1 ? 's' : ''} na fila antes do seu)
                            </>
                          )}
                        </p>
                      ) : (
                        <span className='text-muted fs-7'>
                          Selecione um arquivo para estimar o tempo de análise.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-end pt-15'>
            <button
              type='submit'
              className='btn btn-primary py-2'
              data-kt-contracts-modal-action='submit'
              disabled={
                isContractLoading ||
                formik.isSubmitting ||
                !formik.isValid ||
                !formik.touched ||
                (isNewContract && !formik.values.file)
              }
            >
              <span className='indicator-label'>
                {intl.formatMessage({id: 'FORM.GENERAL.SAVE_BUTTON'})}
              </span>
              {(formik.isSubmitting || isContractLoading) && (
                <span className='indicator-progress'>
                  {intl.formatMessage({id: 'GENERAL.LOADING'})}{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isContractLoading) && <Loading />}
    </>
  )
}

export {ContractsModalContent}
